/**
 * Mensagens de erro do estoque — em português e dizendo O QUE FAZER.
 *
 * O usuário pediu "um fluxo que conduz os operadores e evita erros". Um código
 * como `UNIT_JA_CONSUMIDA` não conduz ninguém: o operador precisa saber que o
 * rolo já saiu e que, se voltou, o caminho é lançar a devolução da sobra.
 *
 * O teste de contrato (tests/stock-errors.test.ts) falha se um código da rota
 * não tiver entrada aqui — código sem mensagem é erro que o operador não sabe
 * resolver, e vira chamado para o gerente.
 */

export interface MensagemErro {
  titulo: string;
  oQueFazer: string;
}

export const STOCK_ERROR_MESSAGES: Record<string, MensagemErro> = {
  PENDENCIA_EXIGE_LANCAMENTO: {
    titulo: 'Esta pendência só fecha com o lançamento',
    oQueFazer: 'Rolo do corte: bipe o rolo usado. Produção: dê entrada nos pacotes pelo card. Marcar como resolvida não corrigiria o saldo.',
  },
  QUANTIDADE_NAO_INTEIRA: {
    titulo: 'Peça se conta inteira',
    oQueFazer: 'Informe um número inteiro de peças — 10, não 10,5. Meia peça não existe no pacote nem na saída.',
  },
  RESERVADO_PARA_OUTRO_PEDIDO: {
    titulo: 'Este material está contado para outro pedido',
    oQueFazer: 'A reserva é de quantidade, não de rolo — mas tirar este rolo deixa o outro pedido sem material. Confirme só se o outro pedido não vai precisar dele, ou pegue outro rolo.',
  },
  BAIXA_AUTOMATICA_JA_FEITA: {
    titulo: 'A costura já deu baixa neste material',
    oQueFazer: 'Quando a costura concluiu, o sistema baixou o que estava reservado para o pedido. Lançar de novo sai em dobro: confirme só se faltou peça de verdade; se a baixa automática foi engano, peça a um gerente para desfazê-la em Movimentos.',
  },
  RATEIO_INVALIDO: {
    titulo: 'Não dá para dividir assim',
    oQueFazer: 'Escolha de 2 a 10 pedidos diferentes para o mesmo rolo. Para um pedido só, use a retirada de sempre.',
  },
  RATEIO_SEM_PECAS: {
    titulo: 'Um pedido não usa esta malha',
    oQueFazer: 'O tecido ou a cor dos itens desse pedido não batem com os do rolo. Tire o pedido da divisão, ou peça ao gerente para acertar o cadastro (o mesmo nome de tecido e cor no item e no material).',
  },
  CORRECAO_EM_ANDAMENTO: {
    titulo: 'A correção já está rodando',
    oQueFazer: 'Outra correção (a das 03h35 ou um clique anterior) ainda não terminou. Espere um minuto e confira o painel — o resultado aparece em "Escritas no ERP".',
  },
  BARCODE_NOT_FOUND: {
    titulo: 'Código não encontrado',
    oQueFazer: 'Confira a etiqueta e bipe de novo. Rolo novo entra por "Entrada de rolo" no posto; pacote de peças, pela expedição ou pelo recebimento.',
  },
  ETIQUETA_CONFLITA_COM_INTERNA: {
    titulo: 'Esta etiqueta parece uma etiqueta do próprio sistema',
    oQueFazer:
      'Etiqueta do fornecedor não pode ter o formato R/P + 7 dígitos nem repetir um código interno já usado. Confira se você bipou a etiqueta certa; se o fornecedor usa esse padrão, deixe o campo vazio e use a etiqueta gerada.',
  },
  ETIQUETA_FORNECEDOR_REPETIDA: {
    titulo: 'Esta etiqueta já está em outro rolo',
    oQueFazer:
      'Cada rolo tem etiqueta própria. Confira se você bipou a etiqueta certa; se o fornecedor mandou dois rolos com a mesma numeração, cadastre este sem a etiqueta dele e cole a etiqueta gerada.',
  },
  BARCODE_AMBIGUO: {
    titulo: 'Etiqueta repetida entre fornecedores',
    // ⚠️ NÃO mande bipar a etiqueta interna. Rolo que chega com QR do
    // fornecedor não recebe etiqueta impressa nossa — imprimir seria trabalho
    // sem ganho —, então "bipe a do sistema" manda o operador procurar um
    // adesivo que não existe. A tela lista os rolos candidatos com material,
    // situação e peso, e o operador escolhe pelo peso do que está na mão.
    oQueFazer: 'Esta numeração está em mais de um rolo. Escolha na lista abaixo pelo peso e pelo material — ou bipe a etiqueta interna, se este rolo tiver uma.',
  },
  MATERIAL_NAO_ENCONTRADO: {
    titulo: 'Material não encontrado',
    oQueFazer: 'Selecione o material na lista. Se ele ainda não existe, cadastre-o primeiro.',
  },
  MATERIAL_INATIVO: {
    titulo: 'Material inativo',
    oQueFazer: 'Reative o material no cadastro antes de movimentá-lo.',
  },
  MATERIAL_DUPLICADO: {
    titulo: 'Material já cadastrado',
    oQueFazer: 'Já existe um material com esse nome ou com esses mesmos atributos. Use o que existe.',
  },
  MATERIAL_EM_USO: {
    titulo: 'Este material já tem histórico',
    // O `motivo` que a rota devolve (já tem movimento / está reservado / está
    // ligado ao GestãoClick…) aparece na própria tela, dentro do formulário. A
    // mensagem aqui é a do toast, que não recebe detalhe — por isso ela diz o
    // caminho de saída, que é sempre o mesmo: cadastrar o material certo.
    oQueFazer: 'Tipo, controle, unidade e atributos (tecido, cor, modelo, tamanho) só mudam enquanto o material não foi usado. Nome, estoque mínimo, bipagem e ativo continuam livres; para o resto, cadastre um material novo e inative este.',
  },
  TRACKING_INCOMPATIVEL: {
    titulo: 'Forma de controle incompatível',
    oQueFazer: 'Rolo é controlado em quilos, pacote em unidades. Confira o tipo de controle do material.',
  },
  UNIT_OBRIGATORIA: {
    titulo: 'Falta bipar o rolo',
    oQueFazer: 'Este material é controlado rolo a rolo — bipe o código em vez de digitar a quantidade.',
  },
  UNIT_MATERIAL_DIVERGENTE: {
    titulo: 'Rolo de outro material',
    oQueFazer: 'O rolo bipado não pertence ao material selecionado. Confira a etiqueta.',
  },
  UNIT_JA_CONSUMIDA: {
    titulo: 'Este rolo não está disponível',
    oQueFazer: 'Ele já saiu para produção ou acabou. Se voltou ao estoque, lance a devolução da sobra.',
  },
  ROLO_EM_USO: {
    titulo: 'Rolo está em produção',
    oQueFazer: 'Para corrigir o peso de um rolo em uso, lance a devolução da sobra com o peso da balança.',
  },
  ALVO_OBRIGATORIO: {
    titulo: 'Falta dizer o que foi contado',
    oQueFazer: 'Bipe o rolo ou o pacote. Só aviamento é ajustado direto pelo material.',
  },
  SALDO_INSUFICIENTE: {
    titulo: 'Não há tudo isso',
    // ⚠️ Texto NEUTRO entre matéria-prima e peça lisa. A versão anterior falava
    // só de material e mandava "registrar a falta para compras" — instrução sem
    // sentido para um pacote de camiseta com menos peças do que a etiqueta diz,
    // e que mandava o operador da separação procurar o comprador. A frase
    // específica (com o código e o número) vem do servidor; esta é o fallback.
    oQueFazer: 'Confira a quantidade. Numa separação, lance o que existe neste pacote e bipe o próximo; numa retirada de material, registre a falta para o setor de compras.',
  },
  SOBRA_MAIOR_QUE_RETIRADA: {
    titulo: 'Sobra maior do que saiu',
    oQueFazer: 'Não é possível devolver mais do que foi retirado. Confira o peso na balança — o ponto decimal costuma ser o culpado.',
  },
  SOBRA_IMPLAUSIVEL: {
    titulo: 'O rolo está voltando quase inteiro',
    oQueFazer: 'Confirme se o peso está certo. Se estiver, confirme para prosseguir.',
  },
  COMPRA_ITEM_NAO_ENCONTRADO: {
    titulo: 'Compra não encontrada',
    oQueFazer: 'Ela pode já ter sido fechada ou cancelada por outra pessoa. Atualize a lista.',
  },
  COMPRA_NADA_RECEBIDO: {
    titulo: 'Nada chegou desta compra ainda',
    oQueFazer: 'O "Não vem mais" fecha a compra com o que já chegou, ou uma linha que não vem quando a compra tem outra linha comprada. Se a compra inteira não vem, cancele-a em Compras.',
  },
  COMPRA_EM_CONFERENCIA: {
    titulo: 'Esta compra está numa conferência aberta',
    oQueFazer: 'Continue a conferência: lá o "O resto ainda vem do fornecedor?" faz o mesmo fechamento.',
  },
  COMPRA_SEM_FALTA: {
    titulo: 'Esta compra já está completa',
    oQueFazer: 'Não falta nada a fechar. Atualize a lista.',
  },
  MOTIVO_OBRIGATORIO: {
    titulo: 'Falta o motivo',
    oQueFazer: 'Diga o motivo (pelo menos 3 letras) — ele fica no histórico e é o que explica a decisão depois.',
  },
  TIPO_NAO_ACEITO: {
    titulo: 'Operação não permitida aqui',
    oQueFazer: 'Por aqui só passam retirada para corte e devolução de sobra. Entrada de rolo é "Entrada de rolo"; correção de contagem é "Ajustar contagem"; desfazer um lançamento é "Desfazer", em Estoque › Lançamentos.',
  },
  ENTRADA_NAO_ESTORNAVEL: {
    titulo: 'Entradas não podem ser desfeitas',
    oQueFazer: 'Desfazer deixaria o item na prateleira sem saldo. Corrija com um ajuste, informando a contagem real.',
  },
  ESTORNO_DE_ESTORNO: {
    titulo: 'Isto já é um estorno',
    oQueFazer: 'Não se desfaz um estorno. Se o saldo ficou errado, faça um ajuste com a contagem correta.',
  },
  ESTORNO_FORA_DA_JANELA: {
    titulo: 'Prazo para desfazer encerrado',
    oQueFazer: 'Passaram-se mais de 30 minutos, ou o lançamento é de outro operador. Peça a um gerente.',
  },
  ESTORNO_EXCEDE_MOVIMENTO: {
    titulo: 'Este movimento já foi desfeito',
    oQueFazer: 'A quantidade a estornar passa do que foi movimentado. Confira o histórico do item.',
  },
  ESTADO_ALTERADO: {
    titulo: 'Há um lançamento mais recente neste item',
    oQueFazer: 'Não dá para desfazer automaticamente. Faça um ajuste com a contagem atual.',
  },
  POSTO_ERRADO: {
    titulo: 'Isto é de outro posto do estoque',
    oQueFazer: 'Passe o item para quem opera esse posto, ou peça ao administrador para incluir o posto no seu usuário (Usuários › Postos de estoque).',
  },
  OBJETO_JA_USADO: {
    titulo: 'Este pacote/rolo já foi usado — não se apaga',
    oQueFazer: 'Excluir de vez só vale para o que nunca teve saída, pedido ou recebimento. Para tirá-lo do saldo, corrija a contagem (zerar): a entrada e o ajuste ficam no histórico.',
  },
  OBJETO_NAO_ENCONTRADO: {
    titulo: 'Pacote/rolo não encontrado',
    oQueFazer: 'Ele pode já ter sido excluído. Bipe a etiqueta: se foi excluído, o sistema diz quando e por quem.',
  },
  MOVIMENTO_NAO_EXCLUIVEL: {
    titulo: 'Este movimento não se exclui',
    oQueFazer: 'Excluir de vez só vale para movimento já desfeito (ele, o estorno e o que foi desfeito entre os dois somem juntos; o saldo não muda). Se ele — ou algo entre ele e o estorno — ainda vale, use Desfazer antes.',
  },
  MOVIMENTO_NAO_ENCONTRADO: {
    titulo: 'Movimento não encontrado',
    oQueFazer: 'Atualize a lista e tente de novo.',
  },
  BATCH_ID_INVALIDO: {
    titulo: 'Erro ao enviar',
    oQueFazer: 'Recarregue a página e refaça o lançamento.',
  },
  SEM_PERMISSAO: {
    titulo: 'Esta ação não é da sua função',
    oQueFazer: 'Peça a um gerente ou a alguém do setor responsável. Se você deveria ter acesso, o gerente ajusta seus setores em Usuários.',
  },
  MATRIZ_NAO_ENCONTRADA: {
    titulo: 'Não encontrei a loja Matriz',
    oQueFazer:
      'Produção para o estoque nasce sempre na Matriz, e o sistema não achou qual loja é ela. Em Integrações › GestãoClick, confira o "id da loja Matriz"; ou garanta que a loja se chame "Matriz" em Lojas.',
  },
  CAPACIDADE_DESLIGADA: {
    titulo: 'Esta função ainda não está ligada',
    oQueFazer:
      'O módulo de estoque está sendo implantado por etapas e esta parte ainda não foi liberada para a sua equipe. Seu trabalho segue pelo caminho de sempre — se a tela apareceu para você, atualize a página (F5). Um gerente libera em Estoque › Implantação.',
  },
  MODO_INVALIDO: {
    titulo: 'Estado inválido para esta função',
    oQueFazer:
      'Recarregue a página (F5) e tente de novo. Se continuar, o painel de implantação está desatualizado em relação ao servidor — avise o suporte antes de mexer nas outras chaves.',
  },
  RECEBIMENTO_NAO_ENCONTRADO: {
    titulo: 'Conferência não encontrada',
    oQueFazer:
      'Ela pode ter sido concluída ou descartada por outra pessoa enquanto esta tela estava aberta. Volte à fila de chegadas e abra de novo — nada do que você pesou se perdeu.',
  },
  DOCUMENTO_NAO_ENCONTRADO: {
    titulo: 'Este documento ainda não está na fila de chegadas',
    oQueFazer:
      'A fila vem do GestãoClick a cada 15 minutos. Confira se a compra foi lançada lá e tente ligar de novo daqui a pouco — o saldo já entrou, nada se perde.',
  },
  RECEBIMENTO_JA_FECHADO: {
    titulo: 'Esta conferência já foi encerrada',
    oQueFazer:
      'Alguém concluiu ou cancelou esta conferência. Não lance de novo, ou os rolos entrariam em dobro no estoque — confira a fila de chegadas para ver o que já entrou.',
  },
  DOCUMENTO_JA_EM_CONFERENCIA: {
    titulo: 'Outra pessoa já está conferindo este documento',
    oQueFazer:
      'Alguém abriu esta mesma nota em outro computador. Fale com quem está conferindo, ou abra a conferência dela pela fila (o botão "retomar") em vez de começar uma segunda.',
  },
  DOCUMENTO_JA_LIGADO: {
    titulo: 'Esta nota já está nesta conferência',
    oQueFazer: 'Escolha outra compra da lista. Para trocar uma nota ligada por engano, use "Trocar nota" na linha dela.',
  },
  DOCUMENTO_NAO_LIGADO: {
    titulo: 'Essa nota não está mais nesta conferência',
    oQueFazer: 'Alguém já a trocou ou desfez. Atualize a página (F5) e veja quais notas a conferência tem agora.',
  },
  NOTA_SEM_ITEM_LIVRE: {
    titulo: 'Todos os itens desta conferência já têm nota',
    oQueFazer: 'Para ligar outra nota, os itens dela precisam estar em linhas separadas na conferência. Uma linha só não se divide entre duas notas.',
  },
  NOTA_NAO_CASOU: {
    titulo: 'A nota não casou com nenhum item desta conferência',
    oQueFazer: 'Confira se é a nota certa. Nada foi alterado.',
  },
  PAR_INVALIDO: {
    titulo: 'A escolha dos itens não vale mais',
    oQueFazer: 'Alguém mexeu na conferência enquanto você escolhia. Abra a nota de novo. Nada foi alterado.',
  },
  NENHUM_ITEM_ESCOLHIDO: {
    titulo: 'Escolha ao menos um item da nota',
    oQueFazer: 'Marque em qual item da conferência cada item da nota entrou, e toque em "Ligar".',
  },
  CONFERENCIA_EM_ANDAMENTO: {
    titulo: 'Esta compra tem uma conferência em andamento',
    oQueFazer:
      'Alguém já conferiu itens desta compra numa conferência aberta. Toque em "Retomar conferência já aberta" e conclua (ou cancele) ela; depois a compra pode ser dada como recebida.',
  },
  ITEM_SEM_VINCULO: {
    titulo: 'Falta dizer que material é',
    oQueFazer:
      'Um ou mais itens da nota ainda não foram ligados a um material do estoque. Ligue cada um — ou use "Não é material de estoque" na linha do que for frete, embalagem ou serviço.',
  },
  SEM_ESTOQUE_INVALIDO: {
    titulo: 'Esta linha não pode ser recebida sem estoque',
    oQueFazer:
      'Só o item de uma compra avulsa (que não é de retrabalho), ainda sem material, pode ser recebido sem estoque. Desfaça a marca e ligue o material, ou toque em "Não veio". Atualize a conferência.',
  },
  RECEBER_NO_POSTO: {
    titulo: 'Esta compra se recebe no posto',
    oQueFazer:
      'A compra tem item sem cadastro de material. Receba no posto (Recebimento) e use "Receber sem estoque" na linha — é lá que a nota fiscal se liga.',
  },
  FALTA_NAO_REGISTRADA: {
    titulo: 'Avise o gerente antes de lançar parcial',
    oQueFazer:
      'Deixar a etapa aberta só resolve se alguém for atrás da peça que falta. Toque em "Conferir o que falta" e escolha o motivo de cada linha — aí o lançamento é liberado.',
  },
  SEPARACAO_AINDA_ABERTA: {
    titulo: 'A separação deste pedido ainda está aberta',
    oQueFazer: 'Repor peça é para pedido já separado. Com a separação aberta, bipe a peça pelo Separar — em "Faltou", use "Usar tamanho maior (reduzir)".',
  },
  FALTA_AGUARDANDO_PRODUCAO: {
    titulo: 'A etapa está esperando a produção',
    oQueFazer:
      'Fechar agora faria a espera sumir e a peça nunca chegaria a este pedido. Para guardar o que já saiu, use "Lançar o que separei" — a etapa fecha quando a produção der entrada.',
  },
  ITEM_COM_ROLO: {
    titulo: 'Esta linha já tem rolo pesado',
    oQueFazer:
      'Remova os rolos desta linha antes de tirá-la da conferência — senão o peso que você já registrou sumiria junto, sem rastro.',
  },
  ITEM_SEM_ROLO: {
    titulo: 'Falta pesar os rolos',
    oQueFazer:
      'Este material é controlado rolo a rolo, e nenhum rolo foi pesado ainda. Pese e etiquete cada rolo antes de concluir — é o peso deles que vira o saldo.',
  },
  DIVERGENCIA_SEM_JUSTIFICATIVA: {
    titulo: 'A quantidade não bate com o esperado',
    oQueFazer:
      'Escreva o que houve (faltou, veio a mais, peça avariada). O saldo entra pelo que você contou de verdade; a justificativa é o que explica a diferença depois — para o fornecedor, no recebimento; para a programação, na produção para o estoque.',
  },
  ROLO_NAO_ENCONTRADO: {
    titulo: 'Este rolo não está mais na lista',
    oQueFazer:
      'Ele pode já ter sido removido. Atualize a tela — se a etiqueta já estava colada no rolo, jogue-a fora e pese de novo.',
  },
  PESO_INVALIDO: {
    titulo: 'Peso não confere',
    oQueFazer:
      'Informe o peso em quilos, maior que zero (ex.: 22,400). Se o rolo está mesmo vazio, não o inclua na conferência.',
  },
  PENDENCIA_NAO_ENCONTRADA: {
    titulo: 'Pendência não encontrada',
    oQueFazer: 'Ela pode ter sido resolvida por outra pessoa. Atualize a lista para ver como está agora.',
  },
  PENDENCIA_JA_RESOLVIDA: {
    titulo: 'Esta pendência já foi tratada',
    oQueFazer:
      'Outra pessoa resolveu ou descartou enquanto a tela estava aberta. Atualize a lista — não é preciso fazer nada.',
  },
  NOTA_OBRIGATORIA: {
    titulo: 'Escreva o motivo do descarte',
    oQueFazer:
      'Descartar sem dizer por quê transforma a fila num lixeiro: daqui a três meses ninguém sabe se aquilo foi resolvido ou varrido para debaixo do tapete.',
  },
  DEMANDA_NAO_ENCONTRADA: {
    titulo: 'Demanda não encontrada',
    oQueFazer: 'Ela pode ter sido cancelada ou atendida por outra pessoa. Atualize a fila para ver como está agora.',
  },
  DEMANDA_JA_ENCERRADA: {
    titulo: 'Esta demanda já foi encerrada',
    oQueFazer:
      'Demanda recebida, atendida ou cancelada não volta atrás. Se ainda falta material para este pedido, abra uma demanda avulsa ligada a ele.',
  },
  ENTREGA_MODO_OBRIGATORIO: {
    titulo: 'Diga como a compra chega',
    oQueFazer: 'Escolha "O fornecedor entrega" ou "Vamos buscar no fornecedor" — quem providencia a retirada precisa saber.',
  },
  FORNECEDOR_OBRIGATORIO: {
    titulo: 'Falta o fornecedor',
    oQueFazer: 'Escolha o fornecedor da lista (ou digite o nome) — é com ele que a mercadoria vem ou é buscada.',
  },
  DATA_PREVISTA_INVALIDA: {
    titulo: 'A data prevista não vale',
    oQueFazer: 'Use uma data de hoje em diante. Se ainda não sabe, deixe em branco e informe depois.',
  },
  INTENCAO_INVALIDA: {
    titulo: 'Escolha entre comprar ou reservar do estoque',
    oQueFazer: 'Use "Comprar em vez de reservar" ou "Reservar do estoque em vez de comprar" no menu do card.',
  },
  ITEM_JA_MOVIMENTADO: {
    titulo: 'Este item já foi reservado, comprado ou recebido',
    oQueFazer: 'A intenção não muda mais. Para mudar o destino, desfaça a reserva em Itens ou acrescente outro item ao pedido.',
  },
  DESCRICAO_INVALIDA: {
    titulo: 'O texto do item não vale',
    oQueFazer: 'Escreva o texto do item e salve sozinho, sem mudar quantidade, material ou intenção na mesma vez.',
  },
  TEXTO_SO_NA_AVULSA: {
    titulo: 'Só o texto de compra avulsa sem pedido pode ser editado',
    oQueFazer: 'Neste item o texto vem do pedido. Para mudar, remova o item e acrescente outro.',
  },
  ITEM_COM_MATERIAL: {
    titulo: 'Este item já está ligado a um material',
    oQueFazer: 'O nome vem do cadastro do material; para mudar, troque o material em "Ligar material".',
  },
  SEM_ITEM_A_COMPRAR: {
    titulo: 'Não há nada a comprar nesta demanda',
    oQueFazer: 'Ela só usa material do estoque. Reserve o material no item; não há compra para marcar.',
  },
  EM_PREPARO: {
    titulo: 'Esta função ainda está sendo preparada',
    oQueFazer: 'Tente de novo em um minuto. O resto do sistema continua funcionando.',
  },
  DEVOLUCAO_NAO_ENCONTRADA: {
    titulo: 'Devolução não encontrada',
    oQueFazer: 'Feche a janela e abra a devolução de novo pela lista.',
  },
  DEVOLUCAO_VAZIA: {
    titulo: 'A devolução ainda não tem rolos',
    oQueFazer: 'Bipe os rolos que vão voltar ao fornecedor e depois ligue a NF.',
  },
  ROLO_JA_DEVOLVIDO: {
    titulo: 'Este rolo já está numa devolução',
    oQueFazer: 'Ele já saiu do estoque. Se foi bipado por engano, use "Desfazer" na lista da devolução.',
  },
  FORNECEDOR_DIFERENTE: {
    titulo: 'Rolo de outro fornecedor',
    oQueFazer: 'Uma NF de devolução vai para um fornecedor só. Separe este rolo e abra outra devolução para ele.',
  },
  NF_NUMERO_INVALIDO: {
    titulo: 'Número de NF inválido',
    oQueFazer: 'Digite só o número da NF, sem a série (ex.: 1234).',
  },
  NF_NAO_ENCONTRADA: {
    titulo: 'NF não encontrada',
    oQueFazer: 'Confira o número e se a NF já foi autorizada no GestãoClick. Depois tente de novo.',
  },
  NF_ESPELHO_DESLIGADO: {
    titulo: 'A leitura das notas fiscais está desligada',
    oQueFazer: 'Peça ao administrador para ligar em Integrações › GestãoClick › Notas fiscais.',
  },
  NF_NAO_E_DEVOLUCAO: {
    titulo: 'Esta NF não é de devolução',
    oQueFazer: 'Confira o número: a NF de devolução ao fornecedor é a de natureza "Devolução de Compra".',
  },
  NF_AMBIGUA: {
    titulo: 'O número existe em mais de uma empresa',
    oQueFazer: 'Escolha de qual empresa é a NF.',
  },
  NF_CANCELADA: {
    titulo: 'NF cancelada',
    oQueFazer: 'Ligue a NF de devolução que valeu.',
  },
  NF_NAO_AUTORIZADA: {
    titulo: 'NF ainda não autorizada',
    oQueFazer: 'Termine a emissão da NF de devolução no GestãoClick e ligue de novo.',
  },
  NF_JA_LIGADA: {
    titulo: 'NF já ligada a outra devolução',
    oQueFazer: 'Confira o número. Cada NF de devolução fica ligada a uma devolução só.',
  },
  TRANSICAO_INVALIDA: {
    titulo: 'Este passo não vem depois do atual',
    oQueFazer:
      'A tela está atrasada em relação ao que já aconteceu: uma demanda segue aberta → em compra → aguardando chegada → recebida, e uma etapa precisa estar em execução para dar entrada. Atualize a tela — outra pessoa pode ter avançado enquanto você olhava.',
  },
  RESERVA_SEM_SALDO: {
    titulo: 'Não há saldo livre para reservar',
    oQueFazer:
      'O que existe já está reservado para outros pedidos. Reserve menos, libere a reserva de um pedido que não vai cortar agora, ou marque este item para comprar.',
  },
  RESERVA_DUPLICADA: {
    titulo: 'Este pedido já tem reserva deste material',
    oQueFazer: 'Altere a reserva existente em vez de criar uma segunda — duas reservas descontariam o saldo em dobro.',
  },
  ITEM_NAO_REMOVIVEL: {
    titulo: 'Este item não pode ser removido',
    oQueFazer: 'Item com reserva ou com material já recebido fica; se é o único item, cancele a demanda inteira (com o motivo).',
  },
  RESERVA_EM_USO: {
    titulo: 'Já saiu material desta reserva',
    oQueFazer:
      'Um rolo (ou parte da quantidade) já foi retirado para o corte com este material. Para trocar, primeiro devolva a sobra ou desfaça a retirada em Estoque › Lançamentos; depois substitua.',
  },
  RESERVA_NAO_ENCONTRADA: {
    titulo: 'Reserva não encontrada',
    oQueFazer: 'Ela pode já ter sido liberada ou consumida pelo corte. Atualize a tela.',
  },
  ITEM_SEM_MATERIAL: {
    titulo: 'Falta dizer que material é',
    oQueFazer: 'Para reservar, o item precisa apontar para um material cadastrado. Vincule o material ou deixe o item como "comprar".',
  },
  PEDIDO_NAO_ENCONTRADO: {
    titulo: 'Pedido não encontrado',
    oQueFazer: 'Ele pode ter sido cancelado ou excluído. Atualize a tela do pedido.',
  },
  ITENS_OBRIGATORIOS: {
    titulo: 'Cadastre os itens antes de programar',
    oQueFazer:
      'Esta produção é para o estoque: abra o card, use "Adicionar Item" (ou "Importar itens") e informe tecido, cor e a quantidade por tamanho. É isso que a expedição vai embalar e etiquetar.',
  },
  JA_PROGRAMADO: {
    titulo: 'Este pedido já foi programado',
    oQueFazer: 'Antecipar compras é só para pedido que ainda espera a programação. As compras dele saem da própria programação — confira em Compras.',
  },
  NADA_A_ANTECIPAR: {
    titulo: 'Nada a comprar ainda',
    oQueFazer: 'Marque ao menos um material como "Comprar" ou "Os dois", ou escreva na observação o que precisa ser comprado.',
  },
  PACOTE_NAO_ENCONTRADO: {
    titulo: 'Este pacote não está no sistema',
    oQueFazer:
      'Confira o código contra a etiqueta. Se o pacote é real e nunca foi cadastrado, chame quem cuida do estoque para dar entrada nele antes de usar.',
  },
  PACOTE_ZERADO: {
    titulo: 'Este pacote já está vazio',
    oQueFazer:
      'Todas as peças dele já saíram. Pegue o próximo pacote — e jogue fora a etiqueta deste, para não bipar de novo por engano.',
  },
  FAMILIA_DIVERGENTE: {
    titulo: 'O pacote bipado não é do que o pedido pede',
    oQueFazer:
      'Confira tecido, cor e modelo do pacote contra o item do pedido. Se for troca combinada com o cliente, confirme para seguir — senão troque o pacote.',
  },
  GRADE_DIVERGENTE: {
    titulo: 'A quantidade não bate com o pedido',
    oQueFazer:
      'Confira se falta bipar algum pacote ou se digitou a quantidade errada. Se o pedido mudou mesmo, confirme para seguir — a diferença fica registrada.',
  },
  NOT_STOCK_ORDER: {
    titulo: 'Este pedido não é produção para o estoque',
    oQueFazer:
      'A entrada automática de pacotes só vale para os cards internos criados pelo comercial como "produzir para o estoque". Um pedido de cliente sai pela expedição normal.',
  },
  STEP_NAO_ENCONTRADO: {
    titulo: 'Etapa não encontrada',
    oQueFazer: 'Ela pode ter sido removida ou o pedido reprogramado. Atualize o quadro.',
  },
  STEP_JA_CONCLUIDO: {
    titulo: 'Esta etapa já foi concluída',
    oQueFazer:
      'As peças já foram registradas. Não bipe de novo, ou sairiam em dobro do estoque — confira o histórico do pedido para ver o que entrou.',
  },
  CAIXA_ERRADA: {
    titulo: 'Caixa errada',
    oQueFazer: 'Esta caixa não tem nada do que este pedido ainda pede. Confira a etiqueta e bipe a caixa certa.',
  },
  SO_TAMANHO_MAIOR: {
    titulo: 'A caixa só tem um tamanho maior',
    oQueFazer: 'Confirme se pode usar o tamanho maior no lugar do que o pedido pede, ou bipe outra caixa.',
  },
  NADA_FALTA_TIRAR: {
    titulo: 'Este pedido já tem todas as peças',
    oQueFazer: 'Nada mais a tirar das caixas neste pedido. Atualize a tela e siga para o resto do corte.',
  },
  RETRABALHO_SEM_CAIXA: {
    titulo: 'Retrabalho não tira das caixas',
    oQueFazer: 'O corte de retrabalho não usa as peças cortadas das caixas por este card. Corte as peças do retrabalho do rolo ou peça ao gerente.',
  },
  BIPE_DESFEITO: {
    titulo: 'Este bipe foi desfeito',
    oQueFazer: 'Bipe a caixa de novo para tirar as peças outra vez.',
  },
  SEM_BIPAGEM: {
    titulo: 'Bipe ao menos um pacote antes de concluir',
    oQueFazer:
      'É o que registra de qual pacote as peças saíram. Se a etiqueta está ilegível, digite o código à mão; se o pacote não existe no sistema, chame o estoque.',
  },
  CATALOGO_GC_VAZIO: {
    titulo: 'O catálogo do GestãoClick ainda não foi sincronizado',
    oQueFazer:
      'A importação lê os produtos que o sistema já espelhou do GestãoClick. Espere a próxima sincronização (roda todo dia) ou peça ao admin para rodar agora.',
  },
  CATALOGO_GC_SEM_SKU: {
    titulo: 'O catálogo veio, mas nenhum produto foi entendido',
    oQueFazer:
      'Não adianta esperar a sincronização — o catálogo já chegou. Para o sistema entender um produto, o NOME DA VARIAÇÃO precisa trazer o tamanho, em qualquer posição ("Branca - M", "Masculino / PP / Branco"); variação sem tamanho reconhecível fica de fora. A cor também precisa estar cadastrada em Cadastros › Cor de malha. A lista abaixo diz o motivo de cada um.',
  },
  VINCULO_AMBIGUO: {
    titulo: 'Mais de um produto do GestãoClick casa com este material',
    oQueFazer:
      'A importação não escolhe no chute. Abra o material e ligue manualmente ao produto certo — depois disso a importação passa a respeitar essa escolha.',
  },
  SKU_SEM_VINCULO_ERP: {
    titulo: 'Peça sem vínculo com o GestãoClick',
    oQueFazer:
      'A entrada no estoque espelha uma compra no GestãoClick, e este SKU não está ligado a nenhum produto de lá. Peça ao gerente para ligar em Estoque › Materiais › "Semear peças lisas do GestãoClick" e tente de novo.',
  },
  ORIGEM_NAO_ACEITA: {
    titulo: 'Origem inválida para criar pacote',
    oQueFazer:
      'Pacote de produção nasce só pela conclusão da expedição do card interno, para o histórico ficar ligado ao que produziu. Aqui você pode registrar inventário, recebimento ou devolução.',
  },
  QUANTIDADE_INVALIDA: {
    titulo: 'Quantidade inválida',
    oQueFazer: 'Informe um número. Use vírgula para os decimais (ex.: 22,4).',
  },
  ERRO_INTERNO: {
    titulo: 'Não foi possível concluir',
    oQueFazer: 'Tente de novo. Se continuar, avise o suporte com o código do item.',
  },
};

export function mensagemDoErro(code: string | undefined | null): MensagemErro {
  if (code && STOCK_ERROR_MESSAGES[code]) return STOCK_ERROR_MESSAGES[code];
  return STOCK_ERROR_MESSAGES.ERRO_INTERNO;
}
